import { ChartBarIcon } from "@phosphor-icons/react";
import { PageHeader } from "@bednbody/ui/components/page-header";
import { StatTile } from "@bednbody/ui/components/stat-tile";

import { GIFTPASS_VALUE_PHP } from "@bednbody/logic";
import { GIFTPASS_BATCH_MAX } from "@bednbody/types";
import { Button } from "@bednbody/ui/components/button";
import {
  AdminDataTable,
  AdminTableIdentity,
  AdminTableRowMenu,
} from "@bednbody/ui/components/admin-data-table";
import { Badge } from "@bednbody/ui/components/badge";
import type { ColumnDef } from "@tanstack/react-table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@bednbody/ui/components/dialog";
import { Input } from "@bednbody/ui/components/input";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@bednbody/ui/components/sheet";
import { PlusIcon } from "@phosphor-icons/react";
import { useState } from "react";
import { useAuth } from "@/lib/auth";
import { ImportPrintedDialog } from "./ImportPrintedDialog";
import { giftpassCsv, giftpassCsvName } from "./giftpass.csv";
import {
  useCreateGiftPassBatch,
  useGiftPassBatches,
  useGiftPasses,
  useInvalidateBatch,
  useReactivatePass,
} from "./giftpass.hooks";

const EMPTY_FORM = { company: "", campaign: "", count: "" };

const KIND_LABEL = { order: "Order", printed: "Printed cards", generated: "Generated" } as const;

type BatchUsage = { status: string; totalPasses: number; used: number };

/** What a batch's cards have done: "Not used yet", "3 of 16 used", "All used". */
function usageLabel(batch: BatchUsage): string {
  if (batch.status !== "active") return "Invalidated";
  if (batch.totalPasses === 0) return "No cards";
  if (batch.used === 0) return "Not used yet";
  if (batch.used >= batch.totalPasses) return "All used";
  return `${batch.used} of ${batch.totalPasses} used`;
}

function usageTone(batch: BatchUsage): "success" | "warning" | "secondary" {
  if (batch.status !== "active") return "secondary";
  if (batch.used >= batch.totalPasses && batch.totalPasses > 0) return "success";
  return batch.used > 0 ? "warning" : "secondary";
}

type Batch = NonNullable<ReturnType<typeof useGiftPassBatches>["data"]>[number];
type Pass = NonNullable<ReturnType<typeof useGiftPasses>["data"]>[number];

/**
 * The Cards tab of the GiftPass page (`embedded`): batches, and every pass in
 * one, used or not and the booking it paid for. Standalone it keeps its own
 * header.
 */
export function GiftPassPage({ embedded = false }: { embedded?: boolean } = {}) {
  const { data: batches, isLoading: batchesLoading } = useGiftPassBatches();
  const [selectedBatch, setSelectedBatch] = useState<string | null>(null);
  const { data: passes, isLoading: passesLoading } = useGiftPasses(selectedBatch);
  const invalidateBatch = useInvalidateBatch();
  const reactivatePass = useReactivatePass();
  const createBatch = useCreateGiftPassBatch();
  const [generating, setGenerating] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [importing, setImporting] = useState(false);
  const isSuper = useAuth().claims?.role === "superadmin";

  const count = Number(form.count);
  const formValid =
    (form.company.trim() !== "" || form.campaign.trim() !== "") &&
    Number.isInteger(count) &&
    count >= 1 &&
    count <= GIFTPASS_BATCH_MAX;

  const openGenerate = () => {
    createBatch.reset();
    setForm(EMPTY_FORM);
    setGenerating(true);
  };

  const submitGenerate = () => {
    if (!formValid) return;
    createBatch.mutate(
      { company: form.company.trim(), campaign: form.campaign.trim(), count },
      {
        onSuccess: (result) => {
          setGenerating(false);
          // Straight to the new passes — the next step is printing them.
          setSelectedBatch(result.batchId);
        },
      },
    );
  };

  const selectedLabel = batches?.find((b) => b.id === selectedBatch)?.name ?? "batch";

  const downloadCodes = (matching: Pass[]) => {
    if (matching.length === 0) return;
    const blob = new Blob([giftpassCsv(matching)], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = giftpassCsvName(selectedLabel);
    link.click();
    window.setTimeout(() => URL.revokeObjectURL(url), 0);
  };

  const handleInvalidateBatch = (batchId: string) => {
    if (confirm("Are you sure you want to invalidate this batch? This cannot be undone.")) {
      invalidateBatch.mutate(batchId);
    }
  };

  const handleReactivatePass = (passId: string) => {
    reactivatePass.mutate(passId);
  };

  const batchColumns: ColumnDef<Batch, unknown>[] = [
    {
      id: "name",
      accessorFn: (batch) => batch.name,
      header: "Batch",
      cell: ({ row }) => (
        <AdminTableIdentity
          title={row.original.name}
          subtitle={[
            KIND_LABEL[row.original.kind],
            row.original.createdAt
              ? new Date(row.original.createdAt).toLocaleDateString("en-PH")
              : null,
          ]
            .filter(Boolean)
            .join(" · ")}
          fallback="GP"
        />
      ),
    },
    { id: "total", accessorFn: (batch) => batch.totalPasses, header: "Cards" },
    {
      id: "status",
      accessorFn: (batch) => usageLabel(batch),
      header: "Used",
      cell: ({ row }) => (
        <Badge variant={usageTone(row.original)}>{usageLabel(row.original)}</Badge>
      ),
    },
    {
      id: "created",
      accessorFn: (batch) => batch.createdAt ?? "",
      header: "Created",
      cell: ({ row }) =>
        row.original.createdAt ? new Date(row.original.createdAt).toLocaleDateString("en-PH") : "—",
    },
    {
      id: "actions",
      header: "",
      enableSorting: false,
      cell: ({ row }) => (
        <AdminTableRowMenu
          label={row.original.name}
          actions={[
            { label: "View passes", onSelect: () => setSelectedBatch(row.original.id) },
            ...(row.original.status === "active"
              ? [
                  {
                    label: "Invalidate batch",
                    onSelect: () => handleInvalidateBatch(row.original.id),
                  },
                ]
              : []),
          ]}
        />
      ),
    },
  ];
  const passColumns: ColumnDef<Pass, unknown>[] = [
    {
      id: "code",
      accessorFn: (pass) => pass.display_code,
      header: "Pass",
      cell: ({ row }) => (
        <AdminTableIdentity
          title={row.original.display_code}
          subtitle={selectedLabel}
          fallback="GP"
        />
      ),
    },
    {
      id: "status",
      accessorFn: (pass) => pass.status,
      header: "Status",
      cell: ({ row }) => (
        <Badge
          variant={
            row.original.status === "active"
              ? "success"
              : row.original.status === "redeemed"
                ? "warning"
                : "secondary"
          }
          className="capitalize"
        >
          {row.original.status.replaceAll("_", " ")}
        </Badge>
      ),
    },
    {
      id: "used",
      accessorFn: (pass) => (pass.status === "redeemed" ? 1 : 0),
      header: "Used",
      // Scanned at checkout but never attached to a booking: the card was
      // spent without coming off a bill (seen 2026-10-29, fixed in #473).
      cell: ({ row }) =>
        row.original.status !== "redeemed" ? (
          "No"
        ) : row.original.redeemed_booking_id ? (
          "Yes"
        ) : (
          <Badge variant="warning">Scanned, no booking</Badge>
        ),
    },
    {
      id: "booking",
      accessorFn: (pass) => pass.booking?.ref ?? "",
      header: "Used on booking",
      cell: ({ row }) => row.original.booking?.ref ?? "—",
    },
    {
      id: "redeemed",
      accessorFn: (pass) => pass.redeemed_at ?? "",
      header: "Used at",
      cell: ({ row }) =>
        row.original.redeemed_at
          ? new Date(row.original.redeemed_at).toLocaleString("en-PH", {
              dateStyle: "medium",
              timeStyle: "short",
            })
          : "—",
    },
    {
      id: "actions",
      header: "",
      enableSorting: false,
      cell: ({ row }) => (
        <AdminTableRowMenu
          label={row.original.display_code}
          actions={
            row.original.status === "revalidation_required"
              ? [{ label: "Reactivate", onSelect: () => handleReactivatePass(row.original.id) }]
              : [
                  {
                    label: "Copy pass ID",
                    onSelect: () => void navigator.clipboard.writeText(row.original.id),
                  },
                ]
          }
        />
      ),
    },
  ];

  if (batchesLoading) {
    return <p className="text-sm text-muted-foreground">Loading GiftPass batches…</p>;
  }

  return (
    <div className="min-w-0 space-y-8">
      {embedded ? null : (
        <PageHeader
          description="Generate, manage, and track GiftPass batches and passes."
          title="GiftPass Management"
        />
      )}

      <div className="grid grid-cols-3 gap-4">
        <StatTile layout="compact" label="Cards" icon={ChartBarIcon}>
          <p className="text-2xl font-semibold text-primary-dark">
            {batches?.reduce((sum, b) => sum + b.totalPasses, 0) || 0}
          </p>
        </StatTile>
        <StatTile layout="compact" label="Used on a booking" icon={ChartBarIcon}>
          <p className="text-2xl font-semibold text-primary-dark">
            {batches?.reduce((sum, b) => sum + b.used, 0) || 0}
          </p>
        </StatTile>
        <StatTile layout="compact" label="Not used yet" icon={ChartBarIcon}>
          <p className="text-2xl font-semibold text-primary-dark">
            {batches?.reduce((sum, b) => sum + b.active, 0) || 0}
          </p>
        </StatTile>
      </div>

      <AdminDataTable
        presentation="standalone"
        data={batches ?? []}
        columns={batchColumns}
        getRowId={(batch) => batch.id}
        searchText={(batch) => [batch.name, batch.status].join(" ")}
        searchPlaceholder="Search batches…"
        header={
          <>
            <Button onClick={openGenerate}>
              <PlusIcon weight="bold" />
              Generate New Batch
            </Button>
            {isSuper ? (
              <Button variant="outline" onClick={() => setImporting(true)}>
                Import printed cards
              </Button>
            ) : null}
          </>
        }
        facets={[
          {
            id: "kind",
            label: "Type",
            options: (["order", "printed", "generated"] as const).map((kind) => ({
              value: kind,
              label: KIND_LABEL[kind],
            })),
            getValue: (batch) => batch.kind,
          },
        ]}
        initialSorting={[{ id: "created", desc: true }]}
        exportFilename="giftpass-batches"
        exportColumns={[
          { header: "Name", value: (batch) => batch.name },
          { header: "Type", value: (batch) => KIND_LABEL[batch.kind] },
          { header: "Cards", value: (batch) => batch.totalPasses },
          { header: "Used", value: (batch) => batch.used },
          { header: "Status", value: (batch) => usageLabel(batch) },
          { header: "Created", value: (batch) => batch.createdAt },
        ]}
      />

      {/* A batch's cards open beside the list, not under it (Leon, 2026-10-30). */}
      <Sheet
        open={selectedBatch !== null}
        onOpenChange={(open) => (open ? null : setSelectedBatch(null))}
      >
        <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-3xl">
          <SheetHeader>
            <SheetTitle>Cards · {selectedLabel}</SheetTitle>
            <SheetDescription>
              Every card in this batch, whether it was used, and the booking it paid for.
            </SheetDescription>
          </SheetHeader>
          <div className="px-4 pb-6">
            <AdminDataTable
              presentation="standalone"
              data={passes ?? []}
              columns={passColumns}
              getRowId={(pass) => pass.id}
              searchText={(pass) => [pass.display_code, pass.status].join(" ")}
              searchPlaceholder="Search pass code or status…"
              facets={[
                {
                  id: "status",
                  label: "Status",
                  options: [
                    "active",
                    "redeemed",
                    "expired",
                    "invalidated",
                    "revalidation_required",
                  ].map((status) => ({ value: status, label: status.replaceAll("_", " ") })),
                  getValue: (pass) => pass.status,
                },
              ]}
              initialSorting={[{ id: "redeemed", desc: true }]}
              loading={passesLoading}
              onExport={downloadCodes}
              exportScopeLabel="Bearer codes — keep the CSV private"
            />
          </div>
        </SheetContent>
      </Sheet>

      <Dialog
        open={generating}
        onOpenChange={(open) => {
          if (!open && !createBatch.isPending) setGenerating(false);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Generate a GiftPass batch</DialogTitle>
            <DialogDescription>
              Each pass is worth ₱{GIFTPASS_VALUE_PHP}, has no expiry, and gets its own printed
              code. Codes are bearer cards — anyone holding one can use it.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <label htmlFor="gp-company" className="block text-sm font-medium">
                Company
              </label>
              <Input
                id="gp-company"
                type="text"
                maxLength={120}
                value={form.company}
                onChange={(e) => setForm((f) => ({ ...f, company: e.target.value }))}
                placeholder="Who the passes are for"
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="gp-campaign" className="block text-sm font-medium">
                Campaign
              </label>
              <Input
                id="gp-campaign"
                type="text"
                maxLength={120}
                value={form.campaign}
                onChange={(e) => setForm((f) => ({ ...f, campaign: e.target.value }))}
                placeholder="Optional if a company is given"
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="gp-count" className="block text-sm font-medium">
                Number of passes
              </label>
              <Input
                id="gp-count"
                type="number"
                inputMode="numeric"
                min={1}
                max={GIFTPASS_BATCH_MAX}
                step={1}
                value={form.count}
                onChange={(e) => setForm((f) => ({ ...f, count: e.target.value }))}
                placeholder={`1–${GIFTPASS_BATCH_MAX}`}
              />
              <p className="text-xs text-muted-foreground">
                Enter a company or a campaign. Up to {GIFTPASS_BATCH_MAX} passes per batch.
              </p>
            </div>

            {createBatch.isError ? (
              <p className="text-sm text-destructive">{createBatch.error.message}</p>
            ) : null}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setGenerating(false)}
              disabled={createBatch.isPending}
            >
              Cancel
            </Button>
            <Button onClick={submitGenerate} disabled={!formValid || createBatch.isPending}>
              {createBatch.isPending
                ? "Generating…"
                : formValid
                  ? `Generate ${count} pass${count === 1 ? "" : "es"}`
                  : "Generate"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ImportPrintedDialog
        open={importing}
        onOpenChange={setImporting}
        onImported={(batchId) => setSelectedBatch(batchId)}
      />
    </div>
  );
}
